/**
 * A thin promise wrapper over IndexedDB. Blobs (recorded audio and video)
 * are stored directly, which is the reason for IndexedDB over localStorage.
 */

const DB_NAME = 'happy-fishy';
const DB_VERSION = 4;

export type StoreName =
  | 'journal'
  | 'spots'
  | 'posts'
  | 'lureNotes'
  | 'replies'
  | 'gear'
  | 'settings'
  | 'outbox';

/**
 * Adding one means bumping DB_VERSION. The upgrade creates whatever is
 * missing and leaves everything already there alone, so an existing member
 * keeps their journal.
 */
const STORES: StoreName[] = [
  'journal',
  'spots',
  'posts',
  'lureNotes',
  'replies',
  'gear',
  'settings',
  'outbox',
];

/**
 * The stores that go to the server. `settings` holds device preferences and
 * sync cursors, and `outbox` is the queue itself, so neither travels.
 */
export type SyncedStore =
  'journal' | 'spots' | 'posts' | 'lureNotes' | 'replies' | 'gear';

export const SYNCED_STORES: SyncedStore[] = [
  'journal',
  'spots',
  'posts',
  'lureNotes',
  'replies',
  'gear',
];

let dbPromise: Promise<IDBDatabase> | null = null;

export function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      for (const name of STORES) {
        if (!db.objectStoreNames.contains(name)) {
          const store = db.createObjectStore(name, { keyPath: 'id' });
          if (name !== 'settings') {
            store.createIndex('createdAt', 'createdAt');
          }
        }
      }
    };
    request.onsuccess = () => {
      const db = request.result;
      db.onversionchange = () => {
        db.close();
        dbPromise = null;
      };
      resolve(db);
    };
    request.onerror = () => reject(request.error);
    request.onblocked = () =>
      reject(new Error('Database upgrade blocked by another open tab'));
  });
  return dbPromise;
}

function promisify<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function done(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error ?? new Error('Transaction aborted'));
  });
}

export async function getAll<T>(store: StoreName): Promise<T[]> {
  const db = await openDb();
  const tx = db.transaction(store, 'readonly');
  return promisify(tx.objectStore(store).getAll() as IDBRequest<T[]>);
}

export async function get<T>(
  store: StoreName,
  id: string,
): Promise<T | undefined> {
  const db = await openDb();
  const tx = db.transaction(store, 'readonly');
  return promisify(tx.objectStore(store).get(id) as IDBRequest<T | undefined>);
}

export async function put<T>(store: StoreName, value: T): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(store, 'readwrite');
  tx.objectStore(store).put(value);
  await done(tx);
}

export async function remove(store: StoreName, id: string): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(store, 'readwrite');
  tx.objectStore(store).delete(id);
  await done(tx);
}

export async function clear(store: StoreName): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(store, 'readwrite');
  tx.objectStore(store).clear();
  await done(tx);
}

export async function clearAll(): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(STORES, 'readwrite');
  for (const name of STORES) tx.objectStore(name).clear();
  await done(tx);
}

/**
 * Reads a row and decides what happens to it in one transaction: return a
 * value to write it, `null` to delete it, `undefined` to leave it alone.
 *
 * For read-then-write that must not race. Two separate transactions leave
 * a gap where another write can land and be overwritten without anyone
 * noticing, which is how the outbox once lost edits.
 */
export async function modify<T>(
  store: StoreName,
  id: string,
  decide: (current: T | undefined) => T | null | undefined,
): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(store, 'readwrite');
  const objects = tx.objectStore(store);
  const request = objects.get(id) as IDBRequest<T | undefined>;
  request.onsuccess = () => {
    const next = decide(request.result);
    if (next === null) objects.delete(id);
    else if (next !== undefined) objects.put(next);
  };
  await done(tx);
}
